<template>
  <div class="form__date">
    <input-row
      v-if="isDateInputSupported"
      v-model="model"
      v-bind="{...$props,...$attrs}"
      type="date"
    />
    <input-row
      v-else
      v-model="model"
      v-bind="{...$props,...$attrs}"
      :input-attrs="inputAttrs"
      type="text"
      :placeholder="datePlaceholder"
      @focus="showHelp"
      @blur="hideHelp"
      @keydown="checkKeys"
    >
      <template #label>
        <slot name="label" />
      </template>
    </input-row>
    <div
      v-if="displayHelp"
      class="form__date__help"
    >
      {{ datePlaceholder }}
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import input from './input-row.vue';
import { DateTime as LuxonDateTime } from 'luxon';

@Component({
  components: {
    'input-row': input,
  }
})
export default class DatePicker extends Vue {
  @Prop({default: ''}) value!: string;
  @Prop({default: false}) useTextInput!: boolean;

  displayHelp!: boolean;
  inputValue!: string;

  data(): any {
    return {
      displayHelp: false,
      inputValue: undefined,
    }
  }

  hideHelp(): void {
    this.displayHelp = false;
  }

  showHelp(): void {
    this.displayHelp = true;
  }

  checkKeys(e: KeyboardEvent): void {
    const value = (e.target as HTMLInputElement).value;

    if (e.key && e.key === '/' && [2,5].indexOf(value.length) === -1) {
      e.preventDefault();
      return;
    }
  }

  get inputAttrs(): any {
    return {...this.$props.inputAttrs, ...{maxlength: 10}};
  }

  get model(): string {
    if (this.isDateInputSupported) {
      return this.value;
    }

    if (!this.inputValue && this.value) {
      this.inputValue = LuxonDateTime.fromFormat(this.value, "yyyy-MM-dd").toFormat("MM/dd/yyyy");
    }

    return this.inputValue;
  }

  set model(v: string) {
    if (this.isDateInputSupported) {
      this.$emit('input', v);
      return;
    }

    if (v.length === 0) {
      this.$emit('input', '');
      return;
    }

    v = v.replace(/\D/g, '');
    if (v.length >= 2) {
      let m = parseInt(v.slice(0, 2));
      if (m > 12) {
        m = 12;
      }
      v = (m < 10 ? '0' + m : m) + "/" + v.slice(2);
    }

    // calculate max day
    const y = (v.length === 9) ? parseInt(v.slice(-4)) : 0;
    let d = parseInt(v.slice(3, 5));

    if (y > 0) {
      const maxDays: number = LuxonDateTime.local(y, parseInt(v.slice(0, 2))).daysInMonth;
      if (d > maxDays) {
        d = maxDays;
      }
    } else {
      if (d > 31) {
        d = 31;
      }
    }

    if (v.length >= 5) {
      v = v.slice(0, 3) + (d < 10 ? '0' + d : d) + "/" + v.slice(5);
    }
    if (v.length > 10) {
      v = v.slice(0, 10);
    }

    const dt = LuxonDateTime.fromFormat(v, "MM/dd/yyyy");

    const now = LuxonDateTime.local();
    const past = LuxonDateTime.local().minus({years: 100});

    if (dt.isValid && dt < now && dt > past) {
      this.$emit('input', dt.toFormat("yyyy-MM-dd"));
    }

    if (dt > now && dt.isValid) {
      v = now.toFormat("MM/dd/yyyy");
    }

    if (dt < past && dt.isValid) {
      v = past.toFormat("MM/dd/yyyy");
    }

    Vue.set(this, 'inputValue', v);
  }

  get isDateInputSupported(): boolean {
    if (this.useTextInput) {
      return false;
    }

    const input = document.createElement('input');
    const value = 'a';
    input.setAttribute('type', 'date');
    input.setAttribute('value', value);

    return input.value !== value;
  }

  get datePlaceholder(): string {
    if (this.isDateInputSupported) {
      return '';
    } else {
      return 'mm/dd/yyyy';
    }
  }
}
</script>

<style scoped>
  .form__date {
    position: relative;
  }

  .form__date__help {
    position: absolute;
    top: 54%;
    font-size: .9em;
    color: #666;
    right: 3px;
  }
</style>
